import React, { useState, SetStateAction, Dispatch } from 'react';
import { Modal, Card, Button, StatusIconProtoProps } from '@nike/eds';
import ManageAccess, { ManageAccessProps } from 'components/shared/manage-access/manage-access';
import styles from './myaccess.module.css';
import { VisitorPass } from '../../models/IVisitorPass';
import { useI18nContext } from '../../context/language/use-i18n-context';
import ts from './i18n';
import { usePassPolicies } from 'context/PassPolicy';
import { Approvers } from 'components/shared/approvers/approvers';
import { Approver } from './type';
import { UpdatePasses, InactivatePass } from 'services/pass-service';
import SwooshTealIcon from 'assets/img/Swoosh-Icon-Teal.png';
import { ManageAccessModal } from 'models/IManageAccess';
import { UpdatePassRequest } from 'models/IPassRequest';
import { AppConfig } from 'utils/constants';
import { HttpStatusCode } from 'axios';

interface MyAccessFormProps {
  accessAccountList: VisitorPass[];
  isChinaRegion: boolean;
  isModalOpen: boolean;
  onDismiss: () => void;
  managerId: string;
  swooshRegistrationLink: string;
  sponsorId: number;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
}

interface ValidateResponse {
  storeChanged: boolean;
  swooshChanged: boolean;
  converseChanged: boolean;
}

const MyAccessForm: React.FC<MyAccessFormProps> = ({
  accessAccountList,
  isModalOpen,
  isChinaRegion,
  onDismiss,
  managerId,
  swooshRegistrationLink,
  sponsorId,
  setSnackBar,
}) => {
  const { i18nString } = useI18nContext();
  const passPoliciesContext = usePassPolicies();
  const [updating, setUpdating] = useState<boolean>(false);

  const [approver, setApprover] = useState<Approver>({
    id: managerId,
    name: '',
  });
  const [showApprover] = useState<boolean>(() => {
    const swooshPolicy =
      passPoliciesContext.passPolicies.filter(x => x.passTypeId === 101)[0] ?? {};
    const conversePolicy =
      passPoliciesContext.passPolicies.filter(x => x.passTypeId === 201)[0] ?? {};

    if ((swooshPolicy?.approvalFlag ?? 0) === 1 || (conversePolicy?.approvalFlag ?? 0) === 1) {
      //if swoosh or converse policy has approval flag, then show approver dropdown
      return true;
    }
    return false;
  });

  const storePass = accessAccountList.find(x => x.passTypeToken === 'PASS_TYPE_EMPLOYEE');
  const swooshPass = accessAccountList.find(x => x.passTypeToken === 'PASS_TYPE_SWOOSH_EMPLOYEE');
  const conversePass = accessAccountList.find(
    x => x.passTypeToken === 'PASS_TYPE_CONVERSE_EMPLOYEE',
  );

  const [swooshFlags] = useState<{
    isSwooshRegistered: boolean;
    isSwooshPassActive: boolean;
    isLinkGenerated: boolean;
  }>(() => {
    return {
      isSwooshRegistered: swooshPass?.passPlayIdRegistered ?? false,
      isSwooshPassActive: swooshPass?.passStatusId === 1,
      isLinkGenerated: swooshRegistrationLink !== '',
    };
  });

  const [validateResponse, setValidateResponse] = useState<ValidateResponse>({
    storeChanged: false,
    swooshChanged: false,
    converseChanged: false,
  });

  const [updatedManageAccessState, setUpdatedManageAccessState] = useState<ManageAccessModal>();
  const [disableUpdateButton, setDisableUpdateButton] = useState<boolean>(true);

  const handleManageAccessStateChange = (state: ManageAccessModal) => {
    setUpdatedManageAccessState(state);
    const { passes: currentPasses } = manageAccessState.manageAccess;
    const { passes: newPasses } = state;

    const validateResponse: ValidateResponse = {
      storeChanged: currentPasses.store.email !== newPasses.store.email,
      swooshChanged:
        currentPasses.swoosh.email !== newPasses.swoosh.email ||
        !currentPasses.swoosh.isActive === newPasses.swoosh.isActive,
      converseChanged:
        currentPasses.converse.email !== newPasses.converse.email ||
        currentPasses.converse.isActive !== newPasses.converse.isActive,
    };

    setValidateResponse(validateResponse);

    if (state.isValid) {
      setDisableUpdateButton(false);
    } else {
      setDisableUpdateButton(true);
    }
  };

  const updateStorePass = (updatePass: UpdatePassRequest) => {
    if (!updatedManageAccessState) {
      return;
    }

    if (validateResponse.storeChanged) {
      updatePass.guestPasses.push({
        emailAddress: updatedManageAccessState.passes.store.email,
        statusId: storePass?.statusId || 0,
        visitorId: storePass?.visitorId || 0,
        alternateId: storePass?.alternateId || '',
        passTypeToken: storePass?.passTypeToken || '',
        passPolicy: storePass?.passPolicy || '',
        passLocation: storePass?.passLocation || '',
        name: storePass?.name || '',
        userId: storePass?.userId || '',
        shoppingTimeSlot: storePass?.shoppingTimeSlot || null,
        employeeId: storePass?.employeeId || '',
        employeeName: storePass?.employeeName || '',
        ReservationConfig: storePass?.ReservationConfig || null,
        slotDescription: storePass?.slotDescription || null,
        passStatusId: storePass?.passStatusId || 0,
        statusToken: storePass?.statusToken || '',
        statusTimestamp: storePass?.statusTimestamp || new Date(),
        relationshipToken: storePass?.relationshipToken || null,
        noteFlagId: storePass?.noteFlagId || null,
        discount: storePass?.discount || 0,
        approverName: storePass?.approverName || '',
        approved: storePass?.approved || false,
        passPlayId: storePass?.passPlayId || null,
        passPlayIdRegistered: storePass?.passPlayIdRegistered || null,
        affiliateId: storePass?.affiliateId || null,
        passId: storePass?.passId || 0,
        sponsorId: storePass?.sponsorId || 0,
        passTypeId: storePass?.passTypeId || 0,
        firstName: storePass?.firstName || '',
        lastName: storePass?.lastName || '',
        shoppingDateStart: storePass?.shoppingDateStart || new Date(),
        shoppingDateEnd: storePass?.shoppingDateEnd || new Date(),
        companyName: storePass?.companyName || null,
        compAmount: storePass?.compAmount || null,
        compNumber: storePass?.compNumber || null,
        passLocationId: storePass?.passLocationId || 0,
        passPolicyId: storePass?.passPolicyId || 0,
        relationshipId: storePass?.relationshipId || null,
        govtComplianceChecked: storePass?.govtComplianceChecked || null,
        timeSlotId: storePass?.timeSlotId || null,
        guestEmailLanguage: storePass?.guestEmailLanguage || '',
      });
    }
  };

  const updateConversePass = async (updatePass: UpdatePassRequest) => {
    if (!updatedManageAccessState) {
      return;
    }
    if (validateResponse.converseChanged) {
      if (updatedManageAccessState.passes.converse.isActive) {
        updatePass.guestPasses.push({
          emailAddress: updatedManageAccessState.passes.converse.email,
          passStatusId: AppConfig.passStatus.registered, //this override is done as per old code
          statusId: AppConfig.passStatus.registered, //this override is done as per old code
          passLocationId: 0, //this override is done as per old code
          visitorId: conversePass?.visitorId || 0,
          alternateId: conversePass?.alternateId || '',
          passTypeToken: conversePass?.passTypeToken || '',
          passPolicy: conversePass?.passPolicy || '',
          passLocation: conversePass?.passLocation || '',
          name: conversePass?.name || '',
          userId: conversePass?.userId || '',
          shoppingTimeSlot: conversePass?.shoppingTimeSlot || null,
          employeeId: conversePass?.employeeId || '',
          employeeName: conversePass?.employeeName || '',
          ReservationConfig: conversePass?.ReservationConfig || null,
          slotDescription: conversePass?.slotDescription || null,
          statusToken: conversePass?.statusToken || '',
          statusTimestamp: conversePass?.statusTimestamp || new Date(),
          relationshipToken: conversePass?.relationshipToken || null,
          noteFlagId: conversePass?.noteFlagId || null,
          discount: conversePass?.discount || 0,
          approverName: conversePass?.approverName || '',
          approved: conversePass?.approved || false,
          passPlayId: conversePass?.passPlayId || null,
          passPlayIdRegistered: conversePass?.passPlayIdRegistered ?? null,
          affiliateId: conversePass?.affiliateId || null,
          passId: conversePass?.passId || 0,
          sponsorId: conversePass?.sponsorId || 0,
          passTypeId: conversePass?.passTypeId || 0,
          firstName: conversePass?.firstName || '',
          lastName: conversePass?.lastName || '',
          shoppingDateStart: conversePass?.shoppingDateStart || new Date(),
          shoppingDateEnd: conversePass?.shoppingDateEnd || new Date(),
          companyName: conversePass?.companyName || null,
          compAmount: conversePass?.compAmount || null,
          compNumber: conversePass?.compNumber || null,
          passPolicyId: conversePass?.passPolicyId || 0,
          relationshipId: conversePass?.relationshipId || null,
          govtComplianceChecked: conversePass?.govtComplianceChecked || null,
          timeSlotId: conversePass?.timeSlotId || null,
          guestEmailLanguage: conversePass?.guestEmailLanguage || '',
        });
      } else {
        try {
          const response = await InactivatePass(conversePass?.passId);
          if (response.status === HttpStatusCode.Ok) {
            setSnackBar({
              showSnack: true,
              message: i18nString(ts.TOASTR_CHANGES_SAVED),
              snackStatus: 'success' as StatusIconProtoProps['status'],
            });
          } else {
            setSnackBar({
              showSnack: true,
              message: i18nString(ts.TOASTR_PASS_ERROR),
              snackStatus: 'error' as StatusIconProtoProps['status'],
            });
          }
          onDismiss();
        } catch (e) {
          setUpdating(false);
          setSnackBar({
            showSnack: true,
            message: i18nString(ts.TOASTR_PASS_ERROR),
            snackStatus: 'error' as StatusIconProtoProps['status'],
          });
          onDismiss();
        }
      }
    }
  };

  const updateSwooshPass = (updatePass: UpdatePassRequest) => {
    if (!updatedManageAccessState) {
      return;
    }
    if (validateResponse.swooshChanged) {
      if (updatedManageAccessState.passes.swoosh.isActive) {
        updatePass.guestPasses.push({
          emailAddress: updatedManageAccessState.passes.swoosh.email,
          passStatusId: AppConfig.passStatus.registered, //this override is done as per old code
          statusId: AppConfig.passStatus.registered, //this override is done as per old code
          passLocationId: 0, //this override is done as per old code
          visitorId: swooshPass?.visitorId || 0,
          alternateId: swooshPass?.alternateId || '',
          passTypeToken: swooshPass?.passTypeToken || '',
          passPolicy: swooshPass?.passPolicy || '',
          passLocation: swooshPass?.passLocation || '',
          name: swooshPass?.name || '',
          userId: swooshPass?.userId || '',
          shoppingTimeSlot: swooshPass?.shoppingTimeSlot || null,
          employeeId: swooshPass?.employeeId || '',
          employeeName: swooshPass?.employeeName || '',
          ReservationConfig: swooshPass?.ReservationConfig || null,
          slotDescription: swooshPass?.slotDescription || null,
          statusToken: swooshPass?.statusToken || '',
          statusTimestamp: swooshPass?.statusTimestamp || new Date(),
          relationshipToken: swooshPass?.relationshipToken || null,
          noteFlagId: swooshPass?.noteFlagId || null,
          discount: swooshPass?.discount || 0,
          approverName: swooshPass?.approverName || '',
          approved: swooshPass?.approved || false,
          passPlayId: swooshPass?.passPlayId || null,
          passPlayIdRegistered: swooshPass?.passPlayIdRegistered ?? null,
          affiliateId: swooshPass?.affiliateId || null,
          passId: swooshPass?.passId || 0,
          sponsorId: swooshPass?.sponsorId || 0,
          passTypeId: swooshPass?.passTypeId || 0,
          firstName: swooshPass?.firstName || '',
          lastName: swooshPass?.lastName || '',
          shoppingDateStart: swooshPass?.shoppingDateStart || new Date(),
          shoppingDateEnd: swooshPass?.shoppingDateEnd || new Date(),
          companyName: swooshPass?.companyName || null,
          compAmount: swooshPass?.compAmount || null,
          compNumber: swooshPass?.compNumber || null,
          passPolicyId: swooshPass?.passPolicyId || 0,
          relationshipId: swooshPass?.relationshipId || null,
          govtComplianceChecked: swooshPass?.govtComplianceChecked || null,
          timeSlotId: swooshPass?.timeSlotId || null,
          guestEmailLanguage: swooshPass?.guestEmailLanguage || '',
        });
      }
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);

    if (!updatedManageAccessState) {
      return;
    }

    const updatePass: UpdatePassRequest = {
      approver: approver.id,
      guestPasses: [],
    };

    //STORE PASS UPDATE
    updateStorePass(updatePass);

    //CONVERSE PASS UPDATE & INACTIVATE
    await updateConversePass(updatePass);

    //SWOOSH PASS UPDATE
    updateSwooshPass(updatePass);

    await callUpdatePassApi(updatePass);

    onDismiss();
    setUpdating(false);
  };

  const callUpdatePassApi = async (updatePassReq: UpdatePassRequest) => {
    if (updatePassReq.guestPasses.length > 0) {
      try {
        const response = await UpdatePasses(sponsorId, updatePassReq);
        if (response.status === HttpStatusCode.Ok) {
          setSnackBar({
            showSnack: true,
            message: i18nString(ts.TOASTR_CHANGES_SAVED),
            snackStatus: 'success' as StatusIconProtoProps['status'],
          });
        } else {
          setSnackBar({
            showSnack: true,
            message: i18nString(ts.TOASTR_PASS_ERROR),
            snackStatus: 'error' as StatusIconProtoProps['status'],
          });
        }
        onDismiss();
      } catch (e) {
        setUpdating(false);
        setSnackBar({
          showSnack: true,
          message: i18nString(ts.TOASTR_PASS_ERROR),
          snackStatus: 'error' as StatusIconProtoProps['status'],
        });
        onDismiss();
      }
    }
  };

  const [manageAccessState] = useState<ManageAccessProps>({
    manageAccess: {
      isChina: isChinaRegion,
      showSwoosh: !!swooshPass,
      showConverse: !!conversePass,
      isValid: false,
      passes: {
        store: {
          isActive: true, //old logic whatever statusToken is store is always active
          email: storePass?.emailAddress || '',
          isDisabled: true,
        },
        swoosh: {
          isActive:
            swooshPass?.statusToken === 'PASS_STATUS_REGISTERED' || swooshPass?.passStatusId === 0,
          email: swooshPass?.emailAddress || '',
          isPendingApproval: swooshPass?.passStatusId === 0,
          isDisabled:
            swooshPass?.statusToken === 'PASS_STATUS_REGISTERED' || swooshPass?.passStatusId === 0,
        },
        converse: {
          isActive: conversePass?.statusToken === 'PASS_STATUS_REGISTERED',
          email: conversePass?.emailAddress || '',
        },
      },
    },
    manageAccessStateChange: handleManageAccessStateChange,
  });

  const handleApproverChange = (option: any) => {
    setApprover({ id: option.value, name: option.label });
  };

  const handleSwooshRegistration = () => {
    window.open(swooshRegistrationLink, '_blank');
  };

  return (
    <Modal
      headerSlot={
        <>
          <center>
            <h1>{i18nString(ts.MANAGEMYACCESS_TEXT)}</h1>
          </center>
        </>
      }
      isOpen={isModalOpen}
      onDismiss={onDismiss}
    >
      <Card className="modal_width">
        {showApprover && (
          <Approvers
            onApproverChange={handleApproverChange}
            noteText={'*' + i18nString(ts.MANAGERAAPROVAL_SWOOSHONLY_MODAL_TEXT)}
          />
        )}
        <section className="eds-background--white eds-spacing--p-20 section-border eds-spacing--mt-12">
          <div className="eds-spacing--mt-12">
            <h4 className={styles.manage_access_header}>{i18nString(ts.REQUESTEDACCESS_TEXT)}</h4>
            <span className={styles.manage_access_text}>{i18nString(ts.SELECT_APPLY_TEXT)}</span>
          </div>
          <ManageAccess
            manageAccess={manageAccessState.manageAccess}
            manageAccessStateChange={manageAccessState.manageAccessStateChange}
          />
          {swooshFlags.isSwooshRegistered && swooshFlags.isSwooshPassActive && (
            <div className="eds-flex eds-flex--justify-content-center eds-flex--align-items-center">
              <div className={`${styles.manage_access_account_linked}`}>
                <img
                  className={styles.access_store_img}
                  src={SwooshTealIcon}
                  alt="Nike Swoosh Icon"
                />
                {i18nString(ts.ACCOUNT_LINKED)}
              </div>
            </div>
          )}
          {swooshFlags.isLinkGenerated && swooshFlags.isSwooshPassActive && (
            <div className="eds-flex eds-flex--justify-content-center eds-flex--align-items-center eds-flex--direction-column">
              <p className={styles.link_account_text}>
                {i18nString(ts.LINKACCOUNT_TEXT_P1)}{' '}
                <a
                  href="https://niketech.service-now.com/nikehr?id=kb_article_view&sys_kb_id=765aa99edbfac918b30c0afed39619e5"
                  className={styles.link_account_link}
                >
                  {i18nString(ts.LINKACCOUNT_TEXT_LINK)}
                </a>{' '}
                {i18nString(ts.LINKACCOUNT_TEXT_P2)}
              </p>
              <br />
              <Button
                variant="primary"
                data-testid="swoosh-link-account"
                className={`txt_uppercase ${styles.link_account_button}`}
                onClick={handleSwooshRegistration}
              >
                {i18nString(ts.LINK_ACCOUNT)}
              </Button>
            </div>
          )}
        </section>
      </Card>
      <div className="eds-flex eds-flex--justify-content-center eds-spacing--m-32">
        {!updating && (
          <Button
            type="submit"
            className="txt_uppercase"
            aria-label="saveCompPassButton"
            variant="primary"
            data-testid="update"
            onClick={handleUpdate}
            disabled={disableUpdateButton}
          >
            {i18nString(ts.BTN_UPDATE)}
          </Button>
        )}
        {updating && <span>{i18nString(ts.SAVING_TEXT)}</span>}
      </div>
    </Modal>
  );
};

export default MyAccessForm;
